import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';

export const CATEGORY_COLORS = {
  nieuws: '#FBBF24',
  helpende_handen: '#F87171',
  opleiding: '#A78BFA',
  giveaway: '#34D399',
};

// Categorieën die een evenement beschrijven: hier tonen we prominent de
// (verplichte) evenementdatum i.p.v. de publicatiedatum, want die laatste
// is bij dit soort posts verwarrend (zie backend EVENT_DATE_CATEGORIES).
export const EVENT_DATE_CATEGORIES = ['helpende_handen', 'giveaway', 'opleiding'];

export function formatDateNL(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-BE', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// Geeft de datum terug die voor deze post getoond moet worden, en of het om
// een evenementdatum gaat (i.p.v. de publicatiedatum).
export function displayDateFor(post) {
  if (EVENT_DATE_CATEGORIES.includes(post.category) && post.eventDate) {
    return { date: post.eventDate, isEventDate: true };
  }
  return { date: post.createdAt, isEventDate: false };
}

function NieuwsCard({ post, t, lang }) {
  const color = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.nieuws;
  const label = t(`news.category_${post.category}`);
  const title = lang === 'fr' ? (post.titleFr || post.titleNl) : (post.titleNl || post.titleFr);
  const thumbnail = post.photo || (post.photos && post.photos[0]);
  const { date, isEventDate } = displayDateFor(post);
  return (
    <Link
      to={`/nieuws/${post.id}`}
      data-testid={`nieuws-card-${post.id}`}
      className="group block border border-border hover:border-foreground transition-colors bg-surface"
    >
      <div className="aspect-[4/3] overflow-hidden">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-white text-3xl font-bold tracking-tight"
            style={{ backgroundColor: color }}
          >
            {label}
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="overline" style={{ color }}>{label}</p>
        <h3 className="mt-2 text-xl font-semibold tracking-tight leading-tight">
          {title}
        </h3>
        <p className="mt-3 text-xs text-muted-foreground">
          {isEventDate && <span className="font-medium text-foreground/70">{t('news.event_date_label')}: </span>}
          {formatDateNL(date)}
        </p>
      </div>
    </Link>
  );
}

export default function Nieuws() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'nl';
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/news', { params: { postType: 'nieuws' } })
      .then(({ data }) => setPosts(data.filter((p) => p.languages?.includes(lang))))
      .catch(() => setError('Kon nieuws niet laden.'));
  }, [lang]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="nieuws-page">
      <p className="overline mb-4">{t('news.subtitle')}</p>
      <h1 className="text-5xl sm:text-6xl font-bold tracking-tight mb-12">{t('news.title')}</h1>

      {error && <p className="text-destructive">{error}</p>}
      {posts === null && !error && <p className="text-muted-foreground">{t('common.loading')}</p>}
      {posts && posts.length === 0 && (
        <p className="text-muted-foreground" data-testid="nieuws-empty">
          {t('news.empty')}
        </p>
      )}

      {posts && posts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <NieuwsCard key={p.id} post={p} t={t} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}
